import React, { useState } from 'react';
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import CommonDialog from './CommonDialog';

const FeedbackForm = ({ open, onClose }) => {
    const [email, setEmail] = useState('');
    const [type, setType] = useState('inquiry');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!email.trim()) {
            setErrorMessage('Please enter your email address');
            return;
        }
        
        if (!email.includes('@')) {
            setErrorMessage('Please enter a valid email address');
            return;
        }
        
        if (!message.trim()) {
            setErrorMessage('Please enter your message');
            return;
        }
        
        setErrorMessage('');
        setSubmitting(true);
        
        try {
            const response = await fetch('/api/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email.trim(),
                    type,
                    message: message.trim(),
                }),
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                setSubmitStatus('success');
                setEmail('');
                setType('inquiry');
                setMessage('');
                // Close dialog after 2 seconds
                setTimeout(() => {
                    setSubmitStatus(null);
                    onClose();
                }, 2000);
            } else {
                setSubmitStatus('error');
                setErrorMessage(data.error || 'Failed to send feedback. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            setSubmitStatus('error');
            setErrorMessage('Failed to send feedback. Please check your connection and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            setEmail('');
            setType('inquiry');
            setMessage('');
            setErrorMessage('');
            setSubmitStatus(null);
            onClose();
        }
    };

    return (
        <CommonDialog
            open={open}
            onClose={handleClose}
            title="📧 Contact Us / Feedback"
            maxWidth="sm"
            PaperProps={{ className: "feedback-dialog-paper" }}
            titleStyle={{
                    padding: '16px 20px',
                    borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, rgba(21, 128, 61, 0.1) 0%, rgba(20, 83, 45, 0.05) 100%)'
            }}
            contentStyle={{
                className: "feedback-dialog-content"
            }}
            onCloseButtonClick={submitting ? undefined : handleClose}
            actions={
                submitStatus !== 'success' ? (
                    <>
                        <Button
                    onClick={handleClose}
                    disabled={submitting}
                            className="feedback-cancel-button"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            form="feedback-form"
                            variant="contained"
                            color="primary"
                            disabled={submitting}
                            className="feedback-submit-button"
                            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                        >
                            {submitting ? 'Sending...' : 'Send'}
                        </Button>
                    </>
                ) : null
            }
            actionsStyle={{
                className: "feedback-dialog-actions"
            }}
        >
                {submitStatus === 'success' ? (
                    <div className="feedback-success-message">
                        <p>✅ Thank you! Your feedback has been sent successfully.</p>
                    </div>
                ) : (
                <form id="feedback-form" onSubmit={handleSubmit} className="feedback-form">
                        <TextField
                            label="Your Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            required
                            margin="normal"
                            variant="outlined"
                            className="feedback-textfield"
                            disabled={submitting}
                            placeholder="your.email@example.com"
                        />
                        
                        <FormControl fullWidth margin="normal" variant="outlined" className="feedback-select">
                            <InputLabel>Type of Contact</InputLabel>
                            <Select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                label="Type of Contact"
                                disabled={submitting}
                            >
                                <MenuItem value="inquiry">General Inquiry</MenuItem>
                                <MenuItem value="improvement">Improvement Suggestion</MenuItem>
                                <MenuItem value="feature">Feature Request</MenuItem>
                                <MenuItem value="bug">Bug Report</MenuItem>
                            </Select>
                        </FormControl>
                        
                        <TextField
                            label="Message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            fullWidth
                            required
                            margin="normal"
                            variant="outlined"
                            multiline
                            rows={6}
                            className="feedback-textfield"
                            disabled={submitting}
                            placeholder="Please share your thoughts, questions, or feedback..."
                        />
                        
                        {errorMessage && (
                            <div className="feedback-error-message">
                                {errorMessage}
                            </div>
                        )}
                    </form>
                )}
        </CommonDialog>
    );
};

export default FeedbackForm;

